// Bulk meta edit modal for Models list multi-select.
// Tags default to Add/Remove; Replace requires an explicit confirm that lists
// every affected model.
import { useMemo, useState } from "react";
import { bulkUpdateManufacturingRecipesMeta } from "@/lib/manufacturingClient";
import { computeRecipeMetaIntersection } from "@/lib/manufacturing/recipeMetaIntersection";
import { parseRecipeTagsInput } from "@/lib/manufacturing/recipeTagsInput";
import { MFG_RECIPE_KIND_OPTIONS } from "@/lib/manufacturing/recipeKinds";
import {
  previewRecipeMetaRename,
  type RecipeMetaRenameField,
} from "@/lib/manufacturing/recipeMetaRenamePreview";
import type { RecipeTagsBulkMode } from "@/lib/manufacturing/recipeMetaTags";
import type { MfgRecipeKind, MfgRecipeSummary } from "@/lib/manufacturing/types";
import {
  ghostBtnClass,
  inputClass,
  mfgPrimaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import { FieldRecipeGroup } from "./ManufacturingModelsMetaFormFields";
import { ManufacturingModelsBulkRenameSection } from "./ManufacturingModelsBulkRenameSection";
import { ManufacturingModelsBulkReplaceTagsConfirm } from "./ManufacturingModelsBulkReplaceTagsConfirm";

type KindChoice = "" | MfgRecipeKind;
type ActiveChoice = "" | "true" | "false";

export function ManufacturingModelsBulkEditModal({
  recipes,
  allRecipes,
  recipeGroups,
  canEditCode,
  onClose,
  onApplied,
  onClearSelection,
}: {
  recipes: readonly MfgRecipeSummary[];
  allRecipes: readonly MfgRecipeSummary[];
  recipeGroups: ReadonlyArray<{ id: string; code: string; name: string }>;
  canEditCode: boolean;
  onClose: () => void;
  onApplied: () => void | Promise<void>;
  onClearSelection: () => void;
}) {
  const intersection = useMemo(() => computeRecipeMetaIntersection(recipes), [recipes]);

  const [kind, setKind] = useState<KindChoice>("");
  const [active, setActive] = useState<ActiveChoice>("");
  const [recipeGroupChoice, setRecipeGroupChoice] = useState("");
  const [tagsEnabled, setTagsEnabled] = useState(false);
  const [tagMode, setTagMode] = useState<RecipeTagsBulkMode>("add");
  const [tagsInput, setTagsInput] = useState("");
  const [renameEnabled, setRenameEnabled] = useState(false);
  const [renameField, setRenameField] = useState<RecipeMetaRenameField>("name");
  const [renameFind, setRenameFind] = useState("");
  const [renameReplace, setRenameReplace] = useState("");
  const [replaceConfirmOpen, setReplaceConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const outsideCodes = useMemo(() => {
    const selected = new Set(recipes.map((r) => r.id));
    return new Set(
      allRecipes
        .filter((r) => !selected.has(r.id))
        .map((r) => r.code.toLowerCase()),
    );
  }, [allRecipes, recipes]);

  const renamePreview = useMemo(() => {
    if (!renameEnabled || !renameFind) return null;
    return previewRecipeMetaRename(
      recipes,
      {
        field: renameField,
        find: renameFind,
        replace: renameReplace,
        caseInsensitive: true,
      },
      { existingCodesOutsideSelection: outsideCodes },
    );
  }, [renameEnabled, renameFind, renameReplace, renameField, recipes, outsideCodes]);

  const tagsParsed = parseRecipeTagsInput(tagsInput);
  const tagsPatchReady =
    tagsEnabled && (tagMode === "replace" || tagsParsed.length > 0);

  const hasPatch =
    kind !== "" ||
    active !== "" ||
    recipeGroupChoice !== "" ||
    tagsPatchReady ||
    (renameEnabled && !!renameFind && (renamePreview?.matchedCount ?? 0) > 0);

  const renameBlocked = Boolean(renameEnabled && renamePreview && renamePreview.blockers.length > 0);

  async function apply() {
    if (!hasPatch || renameBlocked) return;
    setBusy(true);
    setError(null);
    const payload: Parameters<typeof bulkUpdateManufacturingRecipesMeta>[0] = {
      recipe_ids: recipes.map((r) => r.id),
    };
    if (kind) payload.kind = kind;
    if (active === "true") payload.is_active = true;
    if (active === "false") payload.is_active = false;
    if (recipeGroupChoice === "__ungrouped__") payload.recipe_group_id = null;
    else if (recipeGroupChoice) payload.recipe_group_id = recipeGroupChoice;
    if (tagsPatchReady) {
      payload.tags = { mode: tagMode, tags: tagsParsed };
    }
    if (renameEnabled && renameFind && renamePreview && renamePreview.matchedCount > 0) {
      payload.rename = {
        field: renameField,
        find: renameFind,
        replace: renameReplace,
        case_insensitive: true,
      };
    }
    if (
      !payload.kind &&
      payload.is_active === undefined &&
      !("recipe_group_id" in payload) &&
      !payload.tags &&
      !payload.rename
    ) {
      setBusy(false);
      setError("Choose at least one field to change.");
      return;
    }

    const res = await bulkUpdateManufacturingRecipesMeta(payload);
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    const failures = res.data.results.filter((r) => !r.ok);
    if (failures.length > 0) {
      setError(
        `Updated ${res.data.updated_count}; ${failures.length} failed. ` +
          failures
            .slice(0, 3)
            .map((f) => ("error" in f ? f.error : ""))
            .join(" "),
      );
      await onApplied();
      return;
    }
    await onApplied();
    onClearSelection();
  }

  function requestApply() {
    setError(null);
    if (tagsPatchReady && tagMode === "replace") {
      setReplaceConfirmOpen(true);
      return;
    }
    void apply();
  }

  const groupLabel =
    intersection.recipe_group_id === "mixed"
      ? "Mixed"
      : intersection.recipe_group_id == null
        ? "Ungrouped"
        : recipeGroups.find((g) => g.id === intersection.recipe_group_id)?.name ??
          intersection.recipe_group_id;

  if (replaceConfirmOpen) {
    return (
      <ManufacturingModelsBulkReplaceTagsConfirm
        recipes={recipes}
        tagsParsed={tagsParsed}
        busy={busy}
        error={error}
        onConfirm={() => void apply()}
        onBack={() => setReplaceConfirmOpen(false)}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div className="mt-8 w-full max-w-xl rounded-lg border border-gray-200 bg-white p-4 shadow-xl dark:border-zinc-700 dark:bg-zinc-900">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-zinc-100">
              Edit common fields
            </h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-zinc-400">
              {recipes.length} selected. Only fields you change are applied.
            </p>
          </div>
          <button type="button" className={ghostBtnClass} onClick={onClose} disabled={busy}>
            Close
          </button>
        </div>

        <div className="mt-3 rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-700 dark:bg-zinc-800/80 dark:text-zinc-300">
          <div>
            Kind:{" "}
            <span className="font-medium">
              {intersection.kind === "mixed" ? "Mixed" : intersection.kind}
            </span>
            {" · "}
            Active:{" "}
            <span className="font-medium">
              {intersection.is_active === "mixed"
                ? "Mixed"
                : intersection.is_active
                  ? "Yes"
                  : "No"}
            </span>
            {" · "}
            Group: <span className="font-medium">{groupLabel}</span>
          </div>
          <div className="mt-1">
            Tags in common:{" "}
            {intersection.tagsCommon.length > 0
              ? intersection.tagsCommon.join(", ")
              : "(none)"}
          </div>
        </div>

        <div className="mt-4 space-y-3">
          <label className="block text-xs text-gray-600 dark:text-zinc-400">
            Set kind
            <select
              className={`${inputClass} mt-1`}
              value={kind}
              onChange={(e) => setKind(e.target.value as KindChoice)}
            >
              <option value="">(no change)</option>
              {MFG_RECIPE_KIND_OPTIONS.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-xs text-gray-600 dark:text-zinc-400">
            Set active
            <select
              className={`${inputClass} mt-1`}
              value={active}
              onChange={(e) => setActive(e.target.value as ActiveChoice)}
            >
              <option value="">(no change)</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </label>

          <FieldRecipeGroup
            label="Set group"
            value={recipeGroupChoice}
            onChange={setRecipeGroupChoice}
            groups={recipeGroups}
            includeNoChange
          />

          <div className="space-y-2 rounded-md border border-gray-200 p-3 dark:border-zinc-700">
            <label className="inline-flex items-center gap-2 text-xs font-medium text-gray-800 dark:text-zinc-200">
              <input
                type="checkbox"
                checked={tagsEnabled}
                onChange={(e) => setTagsEnabled(e.target.checked)}
              />
              Change tags
            </label>
            {tagsEnabled ? (
              <>
                <div className="flex flex-wrap gap-3 text-xs">
                  {(["add", "remove", "replace"] as const).map((m) => (
                    <label key={m} className="inline-flex items-center gap-1.5 capitalize">
                      <input
                        type="radio"
                        name="tag-mode"
                        checked={tagMode === m}
                        onChange={() => setTagMode(m)}
                      />
                      {m}
                    </label>
                  ))}
                </div>
                {tagMode === "replace" ? (
                  <p className="text-[11px] text-amber-800 dark:text-amber-200">
                    Replace overwrites visible tags on every selected model. You will confirm
                    with the full list before apply.
                  </p>
                ) : null}
                <input
                  className={inputClass}
                  value={tagsInput}
                  onChange={(e) => setTagsInput(e.target.value)}
                  placeholder={
                    tagMode === "replace"
                      ? "New tags (comma-separated), or leave blank to clear"
                      : "Tags (comma-separated)"
                  }
                />
              </>
            ) : null}
          </div>

          <ManufacturingModelsBulkRenameSection
            enabled={renameEnabled}
            onEnabledChange={setRenameEnabled}
            canEditCode={canEditCode}
            renameField={renameField}
            onRenameFieldChange={setRenameField}
            renameFind={renameFind}
            onRenameFindChange={setRenameFind}
            renameReplace={renameReplace}
            onRenameReplaceChange={setRenameReplace}
            renamePreview={renamePreview}
          />
        </div>

        {error ? (
          <div className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</div>
        ) : null}

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            className={mfgPrimaryBtnClass}
            disabled={busy || !hasPatch || renameBlocked}
            onClick={requestApply}
          >
            {busy ? "Applying…" : "Apply to selected"}
          </button>
          <button type="button" className={ghostBtnClass} disabled={busy} onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
